'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Navbar } from '@/components/navbar';
import { Footer } from '@/components/footer';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import Link from 'next/link';

const faqs = [
  {
    category: 'Auto Detailing',
    questions: [
      {
        q: 'How long does the detailing process take?',
        a: 'Express service takes about 1.5 hours, Basic takes 3 hours, and Premium takes 4 hours. These times may vary slightly depending on your vehicle\'s condition.'
      },
      {
        q: 'Do you provide your own equipment and products?',
        a: 'Yes, we bring all professional-grade equipment and premium products. You don\'t need to provide anything except access to your vehicle.'
      },
      {
        q: 'What\'s included in each package?',
        a: 'Express includes basic wash and interior cleaning. Basic adds clay bar treatment, wax, and deep cleaning. Premium includes everything plus paint sealant and advanced restoration services.'
      },
      {
        q: 'Can you detail my car at my location?',
        a: 'Absolutely! We service vehicles at your home, office, or any location convenient for you. We need access to water and a power outlet.'
      },
      {
        q: 'What\'s your cancellation policy?',
        a: 'We require 48 hours notice for cancellations. The $50 deposit is non-refundable but applies toward your service cost if you reschedule.'
      }
    ]
  },
  {
    category: 'Home Inspections',
    questions: [
      {
        q: 'How long does a home inspection take?',
        a: 'A standard inspection takes about 2-3 hours depending on the home\'s size and condition. We provide a thorough assessment of all major systems.'
      },
      {
        q: 'Do I need to be present during the inspection?',
        a: 'Yes, we recommend the homeowner or buyer be present. This allows us to answer questions and show you what we\'ve found.'
      },
      {
        q: 'When will I get my inspection report?',
        a: 'We typically deliver the detailed written report within 24 hours of the inspection, including photos and recommendations.'
      },
      {
        q: 'What does the inspection include?',
        a: 'We inspect the roof, foundation, exterior, electrical, plumbing, HVAC, interior structure, attic, basement, and all major systems and components.'
      },
      {
        q: 'Can you recommend contractors for repairs?',
        a: 'We can provide a list of trusted, qualified contractors if repairs are needed, but the choice is entirely yours.'
      }
    ]
  },
  {
    category: 'Booking & Payment',
    questions: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards, debit cards, and bank transfers for deposits and payments.'
      },
      {
        q: 'Do I need to pay the full amount upfront?',
        a: 'No, you only pay the deposit ($50 for detailing, $200 for inspections) to secure your appointment. The remaining balance is due at the time of service.'
      },
      {
        q: 'How do I reschedule my appointment?',
        a: 'Contact us at least 48 hours before your appointment. We\'ll work with you to find a new time that suits your schedule.'
      },
      {
        q: 'Is the deposit refundable?',
        a: 'The deposit is non-refundable but applies fully toward your service cost. If you cancel, the deposit is forfeited unless rescheduled.'
      },
      {
        q: 'Do you offer discounts for multiple services?',
        a: 'We occasionally offer package deals and referral discounts. Contact us to ask about current promotions!'
      }
    ]
  }
];

export default function FAQPage() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <>
      <Navbar data-zylo-id="zylo-4b649768b267" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="115:7-115:17" data-zylo-component="FAQPage" />

      {/* Hero */}
      <section data-zylo-id="zylo-c346416d10c7" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="118:7-140:17" data-zylo-component="FAQPage" className="bg-gradient-to-b from-primary/10 via-white to-white pt-20 pb-16 sm:pt-32 sm:pb-24">
        <div data-zylo-id="zylo-3ef506c41148" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="119:9-139:15" data-zylo-component="FAQPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-ee1f7b139a8c" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="120:11-138:24" data-zylo-component="FAQPage"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-3xl text-center"
          >
            <motion.h1 data-zylo-id="zylo-db6ad238d45f" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="126:13-131:25" data-zylo-component="FAQPage"
              variants={itemVariants}
              className="text-4xl font-bold sm:text-5xl text-foreground mb-6"
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p data-zylo-id="zylo-f9274dd29452" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="132:13-137:24" data-zylo-component="FAQPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground"
            >
              Find answers to common questions about our services, booking, and pricing.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section data-zylo-id="zylo-10a5be0c9791" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="143:7-221:17" data-zylo-component="FAQPage" className="py-16 sm:py-24">
        <div data-zylo-id="zylo-9e8ea8694f65" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="144:9-220:15" data-zylo-component="FAQPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-f9296e2ae4ee" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="145:11-203:24" data-zylo-component="FAQPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-3xl mx-auto space-y-12"
          >
            {faqs.map((section, sectionIndex) => (
              <motion.div data-zylo-id="zylo-32d75120589e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="153:15-201:28" data-zylo-component="FAQPage" key={sectionIndex} variants={itemVariants}>
                <h2 data-zylo-id="zylo-fd8f08f15393" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="154:17-154:96" data-zylo-component="FAQPage" className="text-2xl font-bold text-foreground mb-6">{section.category}</h2>
                <div data-zylo-id="zylo-2021352526dc" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="155:17-200:23" data-zylo-component="FAQPage" className="space-y-3">
                  {section.questions.map((item, itemIndex) => {
                    const globalIndex = sectionIndex * 10 + itemIndex;
                    const isExpanded = expandedIndex === globalIndex;

                    return (
                      <motion.div data-zylo-id="zylo-7efa30dd425f" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="161:23-197:36" data-zylo-component="FAQPage"
                        key={itemIndex}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                      >
                        <Card data-zylo-id="zylo-99bb559b8bb3" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="167:25-196:32" data-zylo-component="FAQPage" className="border-0 shadow-md overflow-hidden">
                          <button data-zylo-id="zylo-ee8caa510d4d" data-zylo-kind="button" data-zylo-cap="126" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="168:27-179:36" data-zylo-component="FAQPage"
                            onClick={() => setExpandedIndex(isExpanded ? null : globalIndex)}
                            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                          >
                            <span data-zylo-id="zylo-3e82ce4aa1a1" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="172:29-172:97" data-zylo-component="FAQPage" className="font-semibold text-foreground pr-4">{item.q}</span>
                            <motion.div data-zylo-id="zylo-1a95f56276c3" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="173:29-178:42" data-zylo-component="FAQPage"
                              animate={{ rotate: isExpanded ? 180 : 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <ChevronDown data-zylo-id="zylo-e1a00fd1b2b3" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="177:31-177:93" data-zylo-component="FAQPage" className="w-5 h-5 text-primary flex-shrink-0" />
                            </motion.div>
                          </button>

                          <AnimatePresence data-zylo-id="zylo-0038a6c85e4d" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="181:27-195:45" data-zylo-component="FAQPage">
                            {isExpanded && (
                              <motion.div data-zylo-id="zylo-5f662092b6bf" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="183:31-193:44" data-zylo-component="FAQPage"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="border-t border-border"
                              >
                                <div data-zylo-id="zylo-6d4f717b3ca4" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="190:33-192:39" data-zylo-component="FAQPage" className="px-6 py-4 bg-gray-50 text-muted-foreground">
                                  {item.a}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Still Have Questions */}
          <motion.div data-zylo-id="zylo-64ece2e3ebab" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="206:11-219:24" data-zylo-component="FAQPage"
            variants={itemVariants}
            className="mt-16 text-center"
          >
            <Card data-zylo-id="zylo-d5bad1392bd9" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="210:13-218:20" data-zylo-component="FAQPage" className="border-0 shadow-lg p-8 bg-gradient-to-r from-accent/10 to-secondary/10">
              <h3 data-zylo-id="zylo-ad8a0cda78b9" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="211:15-211:97" data-zylo-component="FAQPage" className="text-2xl font-bold text-foreground mb-4">Still have questions?</h3>
              <p data-zylo-id="zylo-1c1d47410ca0" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="212:15-214:19" data-zylo-component="FAQPage" className="text-muted-foreground mb-6">
                Can\'t find what you\'re looking for? We\'re here to help!
              </p>
              <Button data-zylo-id="zylo-7c2bc2873fd0" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="215:15-217:24" data-zylo-component="FAQPage" asChild className="bg-accent hover:bg-accent/90">
                <Link data-zylo-id="zylo-da03e93ee5fc" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="216:17-216:56" data-zylo-component="FAQPage" href="/contact">Contact Us</Link>
              </Button>
            </Card>
          </motion.div>
        </div>
      </section>

      <Footer data-zylo-id="zylo-4961ca987c17" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/faq/page.tsx" data-zylo-loc="223:7-223:17" data-zylo-component="FAQPage" />
    </>
  );
}
